import React from 'react';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom';
import { AuthenticationGuard } from '@trimble-oss/trimble-id-react';
import TIDProviderWithNavigate from '../routers/TIDProviderWithNavigate';
import { createRouteConfig } from '../routers/RouteConfig';
import LandingPage from '../pages/LandingPage/LandingPage';
import CallbackPage from '../pages/CallbackPage/CallbackPage';
import TrimbleSiteLayout from './TrimbleSiteLayout/TrimbleSiteLayout';
import LogoutCallbackPage from '../pages/LogoutCallbackPage/LogoutCallbackPage';
import { ThemeProvider } from '@mui/material/styles';
import { theme } from '@/theme';
import AccountProvider from '@/context/AccountContext';
import environment from '@/environments/environment';
import ErrorPage from '@/pages/ErrorPage/ErrorPage';
import LoadingPage from '@/components/LoadingPage/LoadingPage';
import { ErrorBoundary } from 'react-error-boundary';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { NETWORK_MODE, REFETCH_ON_WINDOW_FOCUS, STALE_TIME } from '@/constants/QueryClient';
import { shouldRetry } from '@/domains/auth.usecase';
import { RouteConfigProvider } from '@/context/RouteConfigContext';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: REFETCH_ON_WINDOW_FOCUS,
      networkMode: NETWORK_MODE,
      retry: shouldRetry,
      staleTime: STALE_TIME,
    },
  },
});

const TIDLayout = () => (
  <TIDProviderWithNavigate>
    <Outlet />
  </TIDProviderWithNavigate>
);

// Use the factory function with explicit empty parentPath for standalone mode
const { routesReactRouter } = createRouteConfig('');

const privateRoutes = routesReactRouter;
const routes = [];
if (!environment.IS_PRODUCTION) {
  routes.push({ index: true, element: <LandingPage /> });
}
routes.push({ path: '/callback', element: <CallbackPage /> });
routes.push({ path: '/logout-callback', element: <LogoutCallbackPage /> });
routes.push(
  ...privateRoutes.map((route, index) => {
    return {
      path: route.path,
      element: (
        <AccountProvider>
          <QueryClientProvider client={queryClient}>
            <ErrorBoundary FallbackComponent={ErrorPage}>
              <TrimbleSiteLayout>
                <AuthenticationGuard renderComponent={route.element} loader={<LoadingPage />} />
              </TrimbleSiteLayout>
            </ErrorBoundary>
          </QueryClientProvider>
        </AccountProvider>
      ),
      children: route.children,
    };
  })
);

const router = createBrowserRouter([
  {
    path: '/',
    Component: TIDLayout,
    errorElement: <ErrorPage />,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    children: routes,
  },
]);

const StandAloneApp = () => {
  return (
    <ThemeProvider theme={theme}>
      <RouteConfigProvider>
        <RouterProvider router={router} />
      </RouteConfigProvider>
    </ThemeProvider>
  );
};

export default StandAloneApp;
